import client from './client';

/**
 * API client for the Kiosk Devices admin page (TAS-585).
 *
 * Consumes the API repo's routes/admin/kioskDevices.ts, per the contract in
 * API/docs/featureBriefs/KioskDeviceEnrollment_02_ApiContract.md:
 *   GET    /admin/kioskDevices
 *   GET    /admin/kioskDevices/:id                 (the code modal polls this every 3 s)
 *   POST   /admin/kioskDevices                     { name, note?, companyID }
 *   PATCH  /admin/kioskDevices/:id                 { name?, note?, companyID?, active? }
 *   DELETE /admin/kioskDevices/:id                 (never-enrolled devices only)
 *   POST   /admin/kioskDevices/:id/enrollmentCode
 *
 * Gated server-side on the ManageKioskDevices permission (admin implies it); a
 * missing permission is a 403 `{ message }`.
 *
 * THE CODE IS RETURNED EXACTLY ONCE — by POST /:id/enrollmentCode. The list and
 * detail reads carry `pendingCode` with its timestamps only. Nothing on the page
 * may persist the code anywhere (state only, dropped when the modal closes).
 */

export type KioskDeviceStatus = 'active' | 'not_enrolled' | 'deactivated';
export type KioskCodePurpose = 'enroll' | 'reenroll';

export interface KioskDeviceAdminRef {
  id: number;
  name: string;
}

/** KioskDeviceDto — defined once in the API (dbInterfaces/KioskDevice.ts). Times are ISO-8601 UTC. */
export interface KioskDevice {
  kioskDeviceID: number;
  name: string;
  note: string | null;
  companyID: string;
  /** !Active → deactivated; FirebaseUID set → active; else not_enrolled. */
  status: KioskDeviceStatus;
  /** FirstEnrolledAt IS NOT NULL. Delete is offered only when this is false. */
  everEnrolled: boolean;
  enrolledAt: string | null;
  lastSeenAt: string | null;
  /** TimeEntry + TimeEntryHourly stamped with this device, tenant-local today. */
  punchesToday: number;
  createdAt: string;
  createdBy: KioskDeviceAdminRef | null;
  deactivatedAt: string | null;
  deactivatedBy: KioskDeviceAdminRef | null;
  /** The live code's timestamps — never the code itself. */
  pendingCode: { issuedAt: string; expiresAt: string; purpose: KioskCodePurpose } | null;
}

/** POST /:id/enrollmentCode — the only response that ever contains the code. */
export interface KioskEnrollmentCode {
  /** Grouped for reading aloud, e.g. "483 920". */
  code: string;
  issuedAt: string;
  expiresAt: string;
  purpose: KioskCodePurpose;
}

export interface KioskDeviceCreateInput {
  name: string;
  note?: string | null;
  companyID: string;
}

export interface KioskDeviceUpdateInput {
  name?: string;
  note?: string | null;
  /** Only while the device has never been enrolled; otherwise 409. */
  companyID?: string;
  /** false deactivates; true reactivates (the device comes back as not_enrolled). */
  active?: boolean;
}

export const getKioskDevices = async (): Promise<KioskDevice[]> => {
  const response = await client.get('/admin/kioskDevices');
  return response.data as KioskDevice[];
};

export const getKioskDevice = async (id: number): Promise<KioskDevice> => {
  const response = await client.get(`/admin/kioskDevices/${id}`);
  return response.data as KioskDevice;
};

export const createKioskDevice = async (input: KioskDeviceCreateInput): Promise<KioskDevice> => {
  const response = await client.post('/admin/kioskDevices', input);
  return response.data as KioskDevice;
};

/** Partial update — only the fields present are written. */
export const updateKioskDevice = async (id: number, input: KioskDeviceUpdateInput): Promise<KioskDevice> => {
  const response = await client.patch(`/admin/kioskDevices/${id}`, input);
  return response.data as KioskDevice;
};

export const setKioskDeviceActive = (id: number, active: boolean): Promise<KioskDevice> =>
  updateKioskDevice(id, { active });

/** Hard delete. 409 when the device was ever enrolled or any punch references it. */
export const deleteKioskDevice = async (id: number): Promise<void> => {
  await client.delete(`/admin/kioskDevices/${id}`);
};

/** Issues a fresh code and cancels any earlier one. 409 when the device is deactivated. */
export const generateKioskEnrollmentCode = async (id: number): Promise<KioskEnrollmentCode> => {
  const response = await client.post(`/admin/kioskDevices/${id}/enrollmentCode`);
  return response.data as KioskEnrollmentCode;
};

/** Server `message` when there is one, else the caller's fallback. */
export function getKioskDeviceErrorMessage(
  error: unknown,
  fallback = "Couldn't save changes. Try again."
): string {
  const msg = (error as any)?.response?.data?.message;
  if (typeof msg === 'string' && msg.trim() !== '') return msg;
  return fallback;
}

/** HTTP status, so the caller can route 409 to an inline error rather than a toast. */
export function getKioskDeviceErrorStatus(error: unknown): number | undefined {
  return (error as any)?.response?.status;
}

/** The 409 body's `field` ("name" when the name is taken by an active device). */
export function getKioskDeviceErrorField(error: unknown): string | undefined {
  const field = (error as any)?.response?.data?.field;
  return typeof field === 'string' ? field : undefined;
}
